import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import Tooltip from '@mui/material/Tooltip'
import { useUiKit } from '../UiKit/UiKitContext'


const LegacyButton = ({ width, size, type, disabled, classes, onClick, children, styles, hover = '' }) => {
  const handleSize = () => {
    width = width || 'max-w-max'

    switch (size) {
      case 'lg':
        return `text-lg px-[22px] py-3.5 ${width}`
      case 'md':
        return `text-md px-4 py-[9px] ${width}`
      case 'sm':
        return `text-sm px-2.5 py-1.5 ${width}`
      default:
        return `text-md px-4 py-[9px] ${width}`
    }
  }

  const createStylesByType = () => {
    let background
    let disabledStyles
    switch (type) {
      case 'text':
        background = 'font-normal text-primary-main'
        hover = hover || 'enabled:hover:bg-primary-main/10'
        disabledStyles = 'disabled:text-text-secondary'
        break
      case 'outlined':
        background = 'font-semibold text-text-primary border border-divider'
        hover = hover || 'enabled:hover:bg-primary-main/10 enabled:hover:border-primary-main'
        disabledStyles = 'disabled:bg-surface disabled:border-divider disabled:text-text-secondary'
        break
      default: //primary is a default type
        background = 'font-semibold bg-primary-main text-primary-contrast'
        hover = hover || 'enabled:hover:bg-primary-main/90 enabled:hover:shadow-[0_8px_16px_0_color-mix(in_srgb,var(--color-primary-main)_24%,transparent)]'
        disabledStyles = 'disabled:bg-surface disabled:border-divider disabled:text-text-secondary'
        break
    }

    return ` rounded-lg ${background} ${hover} ${disabledStyles}`
  }

  return (
    <button className={classnames(handleSize(), createStylesByType(), classes)} disabled={disabled}
      onClick={() => onClick()} type={type} style={styles}>
      {children}
    </button>
  )
}

const KIT_SIZES = {
  lg: 'text-lg px-[22px] py-3.5',
  md: 'text-md px-4 py-[9px]',
  sm: 'text-sm px-2.5 py-1.5',
}

const ICON_SIZES = { lg: 'p-3', md: 'p-2', sm: 'p-1.5' }

const KIT_LOOKS = {
  text: {
    background: 'font-normal text-primary-main',
    hover: 'enabled:hover:bg-primary-main/10',
    disabled: 'disabled:text-text-secondary',
  },
  outlined: {
    background: 'font-semibold text-text-primary border border-divider',
    hover: 'enabled:hover:bg-primary-main/10 enabled:hover:border-primary-main',
    disabled: 'disabled:bg-surface disabled:border-divider disabled:text-text-secondary',
  },
  danger: {
    background: 'font-semibold bg-error-main text-primary-contrast',
    hover: 'enabled:hover:bg-error-main/90',
    disabled: 'disabled:bg-surface disabled:border-divider disabled:text-text-secondary',
  },
  primary: {
    background: 'font-semibold bg-primary-main text-primary-contrast',
    hover: 'enabled:hover:bg-primary-main/90 enabled:hover:shadow-[0_8px_16px_0_color-mix(in_srgb,var(--color-primary-main)_24%,transparent)]',
    disabled: 'disabled:bg-surface disabled:border-divider disabled:text-text-secondary',
  },
}

const FOCUS_RING = 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-main'

const PASS_THROUGH_NAMES = ['title', 'id', 'autoFocus']

const passThrough = (rest) => Object.fromEntries(
  Object.entries(rest).filter(([name]) => /^(aria|data)-/.test(name) || PASS_THROUGH_NAMES.includes(name)),
)

const lookOf = (variant, type) => {
  if (variant === 'danger') return 'danger'
  if (type === 'text' || type === 'outlined') return type
  return 'primary'
}

export const ButtonSpinner = () => (
  <svg data-testid="button-spinner" aria-hidden="true" className="animate-spin h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
    <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
  </svg>
)

const KitButton = ({
  width, size, type, disabled, classes, onClick, children, styles, hover = '',
  variant, loading, disabledReason, iconOnly, ariaLabel, htmlType, ...rest
}) => {
  const [busy, setBusy] = useState(false)
  const mounted = useRef(true)

  useEffect(() => {
    mounted.current = true
    return () => { mounted.current = false }
  }, [])

  const locked = Boolean(loading) || busy
  const look = KIT_LOOKS[lookOf(variant, type)]
  const sizeClasses = iconOnly ? (ICON_SIZES[size] || ICON_SIZES.md) : (KIT_SIZES[size] || KIT_SIZES.md)

  const handleClick = () => {
    if (locked || !onClick) return
    const result = onClick()
    if (result && typeof result.then === 'function') {
      setBusy(true)
      const release = () => { if (mounted.current) setBusy(false) }
      result.then(release, (error) => { release(); throw error })
    }
  }

  const button = (
    <button
      {...passThrough(rest)}
      className={classnames(
        sizeClasses,
        width || 'max-w-max',
        'rounded-lg',
        (locked || iconOnly) && 'inline-flex items-center justify-center gap-2',
        look.background,
        hover || look.hover,
        look.disabled,
        FOCUS_RING,
        classes,
      )}
      disabled={disabled || locked}
      aria-busy={locked || undefined}
      aria-label={iconOnly ? ariaLabel : rest['aria-label']}
      onClick={handleClick}
      type={htmlType || type}
      style={styles}
    >
      {locked && <ButtonSpinner />}
      {iconOnly && locked ? null : children}
    </button>
  )

  if (disabled && disabledReason) {
    return (
      <Tooltip title={disabledReason}>
        <span className="inline-flex" tabIndex={0}>{button}</span>
      </Tooltip>
    )
  }

  if (iconOnly && ariaLabel) {
    return <Tooltip title={ariaLabel}>{button}</Tooltip>
  }

  return button
}

const Button = (props) => {
  const { button: kit } = useUiKit()
  return kit ? <KitButton {...props} /> : <LegacyButton {...props} />
}

Button.propTypes = {
  type: PropTypes.string,
  disabled: PropTypes.bool,
  size: PropTypes.string,
  onClick: PropTypes.func.isRequired,
  width: PropTypes.string,
  styles: PropTypes.object,
  variant: PropTypes.oneOf(['danger']),
  loading: PropTypes.bool,
  disabledReason: PropTypes.string,
  iconOnly: PropTypes.bool,
  ariaLabel: PropTypes.string,
  htmlType: PropTypes.oneOf(['button', 'submit', 'reset']),
}

Button.defaultProps = {
  type: '',
  disabled: false,
  size: '',
  fontSize: '',
  width: '',
  styles: {}
}

export default Button
